import { onearlyRequest } from '@echo/api-client/client';
import { share } from '@echo/api-client/onearly';
import { newIdempotencyKey } from './idempotency';

/**
 * Who a progress summary is shared with (specs/v1-2-0/01-group-progress-and-marks.md, section
 * 1). `GROUP` is the group acting as itself: echo's backend reads it with the group's key, and
 * a plain member doesn't reach it. `GROUP_MEMBERS` expands to every member for reads, which is
 * how installed apps read summaries on the phone (Home › Group progress); it goes once Core
 * refuses those versions (step 4). `GROUP_MEMBER` (singular) is one scoped member, not an
 * audience.
 */
export const SUMMARY_AUDIENCES = ['GROUP', 'GROUP_MEMBERS'] as const;

/**
 * Share a node with the group, through GraphQL's `createShares`: Core's REST route for shares
 * (`POST /api/v1/content/share`) is not in the vendored contract yet. One call names every
 * audience. Core answers each one on its own, in the order sent, so the share counts only when
 * every audience got it. `performAs` must name the node's author (as a member of the group,
 * `scope.ts`); none shares as the person.
 */
export async function shareWithGroup(
  contentUuid: string,
  groupActorUuid: string,
  performAs?: { principalType: 'GROUP_MEMBER'; actorUuid: string },
): Promise<void> {
  const response = await onearlyRequest<{
    data?: {
      errors?: Array<{ message: string }>;
      data?: {
        createShares?: {
          successCount: number;
          failureCount: number;
          results?: Array<{ success: boolean; error?: string | null }>;
        };
      };
    };
  }>('/graphql', {
    method: 'POST',
    body: JSON.stringify({
      query:
        'mutation Share($input: CreateSharesInput!) { createShares(input: $input) { successCount failureCount results { success error } } }',
      variables: {
        input: {
          // Required since core#570 (2026-09-17), which refuses a share without it. A new key
          // per share; a retry of this request re-sends this body, and so the same key.
          idempotencyKey: newIdempotencyKey(),
          shareToPrincipals: SUMMARY_AUDIENCES.map((principalType) => ({
            principalType,
            principalUuid: groupActorUuid,
            permissions: ['READ'],
          })),
          sharedItems: [{ contentUuid }],
          ...(performAs ? { performAs } : {}),
        },
      },
    }),
  });
  const body = response.data;
  const error = body?.errors?.[0]?.message;
  if (error) throw new Error(`OnEarly: share failed — ${error}`);
  const shares = body?.data?.createShares;
  if (!shares || shares.successCount < 1) {
    throw new Error('OnEarly: share failed — no share was created');
  }
  const missing = SUMMARY_AUDIENCES.flatMap((audience, index) => {
    const result = shares.results?.[index];
    if (result?.success) return [];
    return [result?.error ? `${audience} (${result.error})` : audience];
  });
  if (missing.length > 0) {
    throw new Error(`OnEarly: share failed — not shared with ${missing.join(', ')}`);
  }
}

/**
 * A finished exercise for the Stronger Memory group's admins (Core's `GROUP` audience, READ): the
 * attempt with what it contains, and its session row when it is new. Results are written as a
 * member of the group, so they count wherever the exercise was done; leaders of a sub-group and
 * other members never see them, only the weekly summary. `performAs` is the results' author.
 */
export async function shareResultsWithGroup(
  lineageUuids: readonly string[],
  groupActorUuid: string,
  performAs: { principalType: 'GROUP_MEMBER'; actorUuid: string },
): Promise<void> {
  if (lineageUuids.length === 0) return;
  await share({
    idempotencyKey: newIdempotencyKey(),
    lineageUuids: [...lineageUuids],
    grants: [{ principalType: 'GROUP', principalUuid: groupActorUuid, permissions: ['READ'] }],
    includeDescendants: true,
    performAs,
  });
}
